import "./AppCodeAndPreviewComponent.scss";
import React, { useMemo} from "react";
import StreamingCodeComponent
    from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/streaming-code/StreamingCodeComponent.tsx";
import {SandpackInternalOptions} from "@codesandbox/sandpack-react/unstyled";
import {AppType} from "@/models/project.model.ts";
import PreviewRendererComponent
    from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/app-tabs/app-code-and-preview/preview-renderer/PreviewRendererComponent.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";


interface AppCodeAndPreviewComponentProps {
    appType: AppType;
    activeTab: string;
    isFullScreen: boolean;
}

export interface AppConfigurations {
    template: string;
    defaultDependencies?: Record<string, string>;
    options?: SandpackInternalOptions;
    canSadpackPreview: boolean;
}

export type AppConfigurationsMap = {
    [key in keyof typeof appConfigurations]: AppConfigurations;
};

// Example usage:
const appConfigurations: AppConfigurationsMap = {
    nextjs: {
        template: "nextjs",
        defaultDependencies: {
            next: "12.1.6",
            react: "17.0.2",
            "react-dom": "17.0.2",
            tailwindcss: "^3.0.24",
            postcss: "^8.4.14",
            autoprefixer: "^10.4.7",
            uuid: "^8.3.2",
            classnames: "^2.3.1",
            "react-icons": "^4.4.0",
            "framer-motion": "^5.5.0",
            "date-fns": "^2.27.0",
        },
        options: {
            activeFile: "/pages/index.js",
        },
        ignoreBasicFiles: false,
        canSadpackPreview: false,
        loadingFiles: {"/pages/index.js": "// Loading files..."}
    },
    streamlit: {
        template: "node",
        options: {
            activeFile: "app.py",
        },
        ignoreBasicFiles: true,
        canSadpackPreview: false,
        loadingFiles: {"app.py": "// Loading files..."}
    },
    html: {
        template: "node",
        options: {
            activeFile: "index.html",
        },
        ignoreBasicFiles: true,
        canSadpackPreview: false,
        loadingFiles: {"index.html": "// Loading files..."}
    },
};

// Extract this layout function outside the component to avoid recreating it on each render
const codeAndPreviewLayout = (
    children: React.ReactNode,
    activeTab: string,
    isFullScreen: boolean,
    currentTab: string,
    isMobile: boolean
) => {
    const fullScreenHeight = isMobile ? "calc(100vh - 190px)" : "calc(100vh - 85px)";
    const normalHeight = isMobile ? "calc(100vh - 80px)" : "calc(100vh - 85px)";
    return (
        
        <div
            style={{
                position: "relative",
                height: isFullScreen ? normalHeight : fullScreenHeight,
                width: "100%",
                display: activeTab === currentTab ? "block" : "none",
            }}
            className={`code-preview-wrapper ${activeTab}`}
        >
            {children}
        </div>
    );
};

const AppCodeAndPreviewComponent = (props: AppCodeAndPreviewComponentProps) => {
    const {appType, activeTab, isFullScreen} = props;

    const currentAppConfiguration = useMemo(() => appConfigurations[appType], [appType]);

    // Calculate the CSS class outside the render to prevent recalculation
    const componentClass = useMemo(() => (
        'app-code-and-preview-component sandpack-component ' +
        (currentAppConfiguration.ignoreBasicFiles ? " ignore-basic-files" : "")
    ), [currentAppConfiguration.ignoreBasicFiles]);
    
    const isMobile = useIsMobile();


    return (
        <div className={componentClass}>
                    {codeAndPreviewLayout(
                        <StreamingCodeComponent />,
                        activeTab,
                        isFullScreen,
                        "code",
                        isMobile
                    )}
                    {codeAndPreviewLayout(
                        <PreviewRendererComponent/>,
                        activeTab,
                        isFullScreen,
                        "preview",
                        isMobile
                    )}

        </div>
    );
};

export default AppCodeAndPreviewComponent;
